import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Radio,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Snackbar,
  Alert,
} from "@mui/material";

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const question_type_array = {
  1: "Single Choice",
  2: "Multiple Choice",
  3: "True False",
  4: "Fill in the blank",
  5: "Essay Type",
};

const QuestionDisplay = ({
  questionDetails,
  options = [],
  questionIndex,
  savedAnswers = [],
  userAnswerText = "",
  isCorrectAnswerCount = 3,
  onAnswerChange,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState(savedAnswers || []);
  const [typedAnswer, setTypedAnswer] = useState(userAnswerText || "");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const questionTypeId = Number(questionDetails?.question_details?.type);
  const questionTypeName = question_type_array[questionTypeId];

  const isMultipleChoice = questionTypeId === 2;
  const isTextAnswer = questionTypeId === 4 || questionTypeId === 5;

  useEffect(() => {
    setSelectedAnswers(savedAnswers || []);
    setTypedAnswer(userAnswerText || "");
  }, [
    questionDetails?.question_details?.question_id,
    savedAnswers,
    userAnswerText,
  ]);

  const handleOptionChange = (optionId, checked) => {
    if (isMultipleChoice) {
      const updated = checked
        ? [...selectedAnswers, optionId]
        : selectedAnswers.filter((id) => id !== optionId);

      if (updated.length > isCorrectAnswerCount) {
        setSnackbarMessage(
          `You can select only ${isCorrectAnswerCount} option(s)`
        );
        setSnackbarOpen(true);
        return;
      }

      setSelectedAnswers(updated);
      onAnswerChange(updated, typedAnswer);
    } else {
      setSelectedAnswers([optionId]);
      onAnswerChange([optionId], typedAnswer);
    }
  };

  const handleTypedAnswerChange = (e) => {
    const newTypedAnswer = e.target.value || "";
    setTypedAnswer(newTypedAnswer);
    onAnswerChange(selectedAnswers, newTypedAnswer);
  };

  return (
    <Box className="question-section" p={2}>
      <Typography variant="h6" gutterBottom>
        Question {questionIndex + 1}
      </Typography>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: "bold", fontSize: "1.25rem" }}
      >
        {questionDetails?.question_details?.question_text}
      </Typography>

      {isTextAnswer ? (
        <>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={typedAnswer}
            onChange={handleTypedAnswerChange}
            placeholder="Type your answer here"
          />
          {options.length > 0 && (
            <input type="hidden" name="ans_optionid" value={options[0].id} />
          )}
        </>
      ) : isMultipleChoice ? (
        <FormGroup>
          {options.map((option, idx) => (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={selectedAnswers.includes(option.id)}
                  onChange={(e) =>
                    handleOptionChange(option.id, e.target.checked)
                  }
                />
              }
              label={
                <>
                  <strong>{letters[idx]}. </strong> {option.answer_text}
                </>
              }
            />
          ))}
        </FormGroup>
      ) : (
        <RadioGroup
          value={selectedAnswers[0] || ""}
          onChange={(e) => handleOptionChange(Number(e.target.value), true)}
        >
          {options.map((option, idx) => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              control={<Radio />}
              label={
                <>
                  <strong>{letters[idx]}. </strong> {option.answer_text}
                </>
              }
            />
          ))}
        </RadioGroup>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionDisplay;
